// src/components/chat/WebToolsSection.tsx

import * as React from "react";
import { WebToolEvent } from "../../interfaces/webToolTypes";
import { createLogger } from "../../utils/logger";
import WebSearchItem from "./WebSearchItem";
import WebFetchItem from "./WebFetchItem";

const log = createLogger("WebToolsSection");

interface WebToolsSectionProps {
  toolEvents: WebToolEvent[];
  isSearching: boolean;
  isFetching: boolean;
  isBenchmarkMode?: boolean;
}

/**
 * Unified component that displays both web search and web fetch activity
 * Combines search queries and fetched pages in a single collapsible section
 */
const WebToolsSection: React.FC<WebToolsSectionProps> = ({
  toolEvents,
  isSearching,
  isFetching,
  isBenchmarkMode = false,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  // Log component mount with tool state
  React.useEffect(() => {
    log.info("🖼️ WebToolsSection mounted", {
      toolEventCount: toolEvents.length,
      isSearching,
      isFetching,
      isBenchmarkMode,
      searchEventCount: searchCount,
      fetchEventCount: fetchCount,
      toolEventsDetail: toolEvents,
      fetchEvents: toolEvents.filter((e) => e.toolType === "web_fetch"),
    });
  }, []); // Empty deps = mount only

  // Count events by type
  const searchCount = toolEvents.filter((e) => e.toolType === "web_search").length;
  const fetchCount = toolEvents.filter((e) => e.toolType === "web_fetch").length;

  // Get header status text
  const getStatusText = () => {
    if (isSearching && isFetching) {
      return isBenchmarkMode
        ? "Searching industry benchmarks and fetching sources..."
        : "Searching the web and fetching sources...";
    } else if (isSearching) {
      return isBenchmarkMode ? "Searching industry benchmarks..." : "Searching the web...";
    } else if (isFetching) {
      return "Fetching sources...";
    }

    // Completed state
    const parts: string[] = [];
    if (searchCount > 0) {
      const searchType = isBenchmarkMode ? "industry benchmarks" : "the web";
      parts.push(`Searched ${searchType} ${searchCount} time${searchCount !== 1 ? "s" : ""}`);
    }
    if (fetchCount > 0) {
      parts.push(`Fetched ${fetchCount} source${fetchCount !== 1 ? "s" : ""}`);
    }

    return parts.join(" · ") || "No web tools used";
  };

  // Container styles
  const containerStyle: React.CSSProperties = {
    marginBottom: "12px",
  };

  const headerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px 16px",
    backgroundColor: "#F8F9FA",
    borderRadius: isExpanded ? "8px 8px 0 0" : "8px",
    border: "1px solid #E9ECEF",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
    userSelect: "none",
    fontFamily: "'Segoe UI', sans-serif",
  };

  const contentStyle: React.CSSProperties = {
    backgroundColor: "#FFFFFF",
    borderTop: "none",
    borderRight: isExpanded ? "1px solid #E9ECEF" : "none",
    borderBottom: isExpanded ? "1px solid #E9ECEF" : "none",
    borderLeft: isExpanded ? "1px solid #E9ECEF" : "none",
    borderRadius: isExpanded ? "0 0 8px 8px" : "0",
    maxHeight: isExpanded ? "800px" : "0",
    overflow: "hidden",
    transition: "max-height 0.3s ease",
  };

  const messageStyle: React.CSSProperties = {
    flex: 1,
    fontSize: "13px",
    color: "#495057",
    fontWeight: 500,
  };

  return (
    <div style={containerStyle}>
      {/* Collapsible Header */}
      <div
        style={headerStyle}
        onClick={() => setIsExpanded(!isExpanded)}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.backgroundColor = "#E9ECEF";
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.backgroundColor = "#F8F9FA";
        }}
      >
        {/* Expand/collapse arrow */}
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{
            transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
            flexShrink: 0,
          }}
        >
          <path
            d="M9 18L15 12L9 6"
            stroke="#6C757D"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>

        {/* Status message */}
        <div style={messageStyle}>{getStatusText()}</div>
      </div>

      {/* Expandable Content */}
      <div style={contentStyle}>
        <div style={{ padding: "16px" }}>
          {toolEvents.map((event, index) => {
            const isLast = index === toolEvents.length - 1;
            const showConnector = index < toolEvents.length - 1;

            // Render appropriate component based on event type
            if (event.toolType === "web_search") {
              return (
                <WebSearchItem
                  key={event.id}
                  event={event}
                  isLast={isLast}
                  showConnector={showConnector}
                />
              );
            } else {
              // web_fetch
              return (
                <WebFetchItem
                  key={event.id}
                  event={event}
                  isLast={isLast}
                  showConnector={showConnector}
                />
              );
            }
          })}
        </div>
      </div>
    </div>
  );
};

export default WebToolsSection;
